import { Button, Checkbox, DatePicker, Icon, Input, Select, useToast } from '@/components';
import { useAuth } from '@/features/auth/AuthProvider';
import { cn } from '@/lib/cn';
import {
  ApiClientError,
  type CreateDeviceBody,
  type DeviceOs,
  type DeviceRow,
  type ImeiSearchMatch,
} from '@uchat-team-management/shared';
import { For, Show, createMemo, createSignal, onCleanup } from 'solid-js';
import { uploadDeviceFile } from './api';
import { useAssignDevice, useCreateDevice, useImeiSearch } from './hooks';

interface Props {
  onCancel: () => void;
  onRegistered: (device: DeviceRow) => void;
  onClaimed: (deviceId: string) => void;
  /** Invoked from the duplicate-IMEI panel; only offered to devices:list holders. */
  onViewDevice: (deviceId: string) => void;
  /** 'always' hides the checkbox and always self-assigns (register page). */
  selfAssign?: 'checkbox' | 'always';
  /** Two-column layout for full-width pages. */
  wide?: boolean;
  /** Stage files that upload to the system file groups right after creation. */
  withPhotos?: boolean;
}

// UCH-437: one staged-upload section per seeded system group (apps/api/src/db/seed.ts).
const STAGED_GROUPS = [
  { slug: 'photos', label: 'Photos', accept: 'image/*' },
  { slug: 'box-photos', label: 'Box Photos', accept: 'image/*' },
  { slug: 'receipts', label: 'Receipts', accept: 'image/*,application/pdf' },
] as const;

export const RegisterDeviceForm = (props: Props) => {
  const create = useCreateDevice();
  const toast = useToast();
  const { session } = useAuth();
  // Callers who can see all IMEI matches get match details + "Use anyway";
  // others only ever see unassigned matches (server-filtered) and may claim.
  const seesAllMatches = () => session()?.permissions.has('devices:searchByImei:all') ?? false;
  const canViewDevice = () => session()?.permissions.has('devices:list') ?? false;

  const [name, setName] = createSignal('');
  const [model, setModel] = createSignal('');
  const [os, setOs] = createSignal<DeviceOs>('ios');
  const [serial, setSerial] = createSignal('');
  const [imei, setImei] = createSignal('');
  const [debouncedImei, setDebouncedImei] = createSignal('');
  const [purchaseDate, setPurchaseDate] = createSignal('');
  const [warrantyExpiresAt, setWarrantyExpiresAt] = createSignal('');
  const [assignToSelf, setAssignToSelf] = createSignal(false);
  const [error, setError] = createSignal<string | null>(null);
  const [warningOverridden, setWarningOverridden] = createSignal(false);
  const [staged, setStaged] = createSignal<Record<string, File[]>>({});
  let debounceTimer: ReturnType<typeof setTimeout> | undefined;

  const onImeiInput = (v: string) => {
    setImei(v);
    setWarningOverridden(false);
    if (debounceTimer) clearTimeout(debounceTimer);
    debounceTimer = setTimeout(() => setDebouncedImei(v.trim()), 250);
  };

  const search = useImeiSearch(debouncedImei);
  const firstMatch = createMemo<ImeiSearchMatch | null>(() => {
    const data = search.data?.data;
    return data && data.length > 0 ? (data[0] as ImeiSearchMatch) : null;
  });
  const showWarning = () => !warningOverridden() && firstMatch() != null;

  const claim = useAssignDevice(firstMatch()?.id ?? '');

  const onSubmit = async (e: Event) => {
    e.preventDefault();
    setError(null);
    if (!name().trim()) {
      setError('Name is required');
      return;
    }
    const body: CreateDeviceBody = {
      name: name().trim(),
      model: model().trim(),
      os: os(),
      serial: serial().trim(),
      imei: imei().trim(),
      notes: '',
      purchaseDate: purchaseDate() || undefined,
      warrantyExpiresAt: warrantyExpiresAt() || undefined,
      assignToSelf: props.selfAssign === 'always' ? true : assignToSelf() || undefined,
    };
    try {
      const resp = await create.mutateAsync(body);
      if (props.withPhotos) {
        let failed = 0;
        for (const group of STAGED_GROUPS) {
          for (const file of staged()[group.slug] ?? []) {
            try {
              await uploadDeviceFile(resp.data.id, group.slug, file);
            } catch {
              failed++;
            }
          }
        }
        if (failed > 0) toast.error(`${failed} file${failed === 1 ? '' : 's'} failed to upload`);
      }
      toast.success('Device registered');
      props.onRegistered(resp.data);
    } catch (err) {
      if (err instanceof ApiClientError) {
        setError(err.error.message);
      } else {
        setError((err as Error).message ?? 'Register failed');
      }
    }
  };

  const onClaim = async () => {
    const match = firstMatch();
    const user = session();
    if (!match || !user) return;
    try {
      // useAssignDevice is bound to the matched device id via closure.
      await claim.mutateAsync({ userId: user.id });
      toast.success('Device claimed');
      props.onClaimed(match.id);
    } catch {
      toast.error('Claim failed');
    }
  };

  return (
    <form
      onSubmit={onSubmit}
      class={cn('gap-4', props.wide ? 'grid items-start md:grid-cols-2' : 'flex flex-col')}
    >
      <div class="md:col-span-2">
        <Input
          label="IMEI"
          value={imei()}
          onInput={(e) => onImeiInput(e.currentTarget.value)}
          autofocus
          required={!(session()?.permissions.has('devices:registerWithoutImei') ?? false)}
          hint="Type the IMEI — we'll check for duplicates before you register."
        />
      </div>

      <Show when={showWarning()}>
        {(_) => {
          const match = firstMatch();
          if (!match) return null;
          return (
            <div class="rounded-md border border-amber-300 bg-amber-50 p-3 text-sm md:col-span-2 dark:border-amber-700 dark:bg-amber-900/30">
              <Show
                when={seesAllMatches()}
                fallback={
                  <>
                    <p>This IMEI is already registered — unassigned.</p>
                    <div class="mt-2 flex gap-2">
                      <Button size="sm" onClick={onClaim} loading={claim.isPending}>
                        Claim instead
                      </Button>
                      <Button size="sm" variant="ghost" onClick={props.onCancel}>
                        Cancel
                      </Button>
                    </div>
                  </>
                }
              >
                <p>
                  This IMEI is already registered as <strong>{match.name}</strong>
                  {match.assignedUserName
                    ? `, assigned to ${match.assignedUserName}`
                    : ', unassigned'}
                  .
                </p>
                <div class="mt-2 flex gap-2">
                  <Show when={canViewDevice()}>
                    <Button size="sm" onClick={() => props.onViewDevice(match.id)}>
                      View device
                    </Button>
                  </Show>
                  <Button size="sm" variant="ghost" onClick={() => setWarningOverridden(true)}>
                    Use anyway
                  </Button>
                </div>
              </Show>
            </div>
          );
        }}
      </Show>

      <Input label="Name" value={name()} onInput={(e) => setName(e.currentTarget.value)} required />
      <Input label="Model" value={model()} onInput={(e) => setModel(e.currentTarget.value)} />
      <Select
        label="OS"
        value={os()}
        onChange={(v) => setOs(v as DeviceOs)}
        options={[
          { value: 'ios', label: 'iOS' },
          { value: 'android', label: 'Android' },
          { value: 'other', label: 'Other' },
        ]}
      />
      <Input label="Serial" value={serial()} onInput={(e) => setSerial(e.currentTarget.value)} />
      <DatePicker label="Purchase date" value={purchaseDate()} onChange={setPurchaseDate} />
      <DatePicker
        label="Warranty expires"
        value={warrantyExpiresAt()}
        onChange={setWarrantyExpiresAt}
      />
      <Show when={props.selfAssign !== 'always'}>
        <Checkbox checked={assignToSelf()} onChange={setAssignToSelf} label="Assign to me" />
      </Show>
      <Show when={props.withPhotos}>
        <For each={STAGED_GROUPS}>
          {(group) => (
            <StagedFilesSection
              label={group.label}
              accept={group.accept}
              files={staged()[group.slug] ?? []}
              onChange={(files) => setStaged((prev) => ({ ...prev, [group.slug]: files }))}
            />
          )}
        </For>
      </Show>
      <Show when={error()}>
        <p class="text-xs text-red-600 md:col-span-2">{error()}</p>
      </Show>
      <div class="flex justify-end gap-2 md:col-span-2">
        <Button variant="ghost" type="button" onClick={props.onCancel}>
          Cancel
        </Button>
        <Button
          type="submit"
          loading={create.isPending}
          disabled={showWarning() && !seesAllMatches()}
        >
          Register
        </Button>
      </div>
    </form>
  );
};

const StagedFilesSection = (props: {
  label: string;
  accept: string;
  files: File[];
  onChange: (files: File[]) => void;
}) => {
  let inputRef: HTMLInputElement | undefined;
  // The staging filter mirrors the picker's accept attr so the two can't drift.
  const allowPdf = () => props.accept.includes('application/pdf');

  // Object URLs for image previews; revoked when the list changes or on unmount.
  // Non-image files (receipts PDFs) get a filename tile instead.
  const previews = createMemo<{ file: File; url: string | null }[]>((prev) => {
    for (const p of prev ?? []) if (p.url) URL.revokeObjectURL(p.url);
    return props.files.map((file) => ({
      file,
      url: file.type.startsWith('image/') ? URL.createObjectURL(file) : null,
    }));
  });
  onCleanup(() => {
    for (const p of previews()) if (p.url) URL.revokeObjectURL(p.url);
  });

  const onChosen = (e: Event) => {
    const input = e.currentTarget as HTMLInputElement;
    const files = Array.from(input.files ?? []).filter(
      (f) => f.type.startsWith('image/') || (allowPdf() && f.type === 'application/pdf'),
    );
    input.value = '';
    if (files.length) props.onChange([...props.files, ...files]);
  };

  return (
    <div class="flex flex-col gap-2 md:col-span-2">
      <span class="text-sm font-medium text-neutral-700 dark:text-neutral-300">{props.label}</span>
      <div class="flex flex-wrap gap-3">
        <For each={previews()}>
          {(item, i) => (
            <div class="group relative">
              <Show
                when={item.url}
                fallback={
                  <div class="flex h-24 w-24 items-center justify-center overflow-hidden rounded-lg border border-neutral-300 p-1 text-center text-neutral-500 text-xs break-all dark:border-neutral-700">
                    {item.file.name}
                  </div>
                }
              >
                {(url) => (
                  <img src={url()} alt={item.file.name} class="h-24 w-24 rounded-lg object-cover" />
                )}
              </Show>
              <button
                type="button"
                aria-label={`Remove ${item.file.name}`}
                class="-right-2 -top-2 absolute hidden rounded-full bg-red-600 p-1 text-xs text-white group-hover:block"
                onClick={() => props.onChange(props.files.filter((_, idx) => idx !== i()))}
              >
                <Icon name="cross-small" class="h-3 w-3" />
              </button>
            </div>
          )}
        </For>
        <button
          type="button"
          onClick={() => inputRef?.click()}
          class="flex h-24 w-24 flex-col items-center justify-center rounded-lg border border-neutral-300 border-dashed text-neutral-500 text-xs transition-colors hover:border-brand-400 hover:text-brand-600 dark:border-neutral-700 dark:hover:text-brand-400"
        >
          {allowPdf() ? '+ Add file' : '+ Add photo'}
        </button>
        <input
          ref={inputRef}
          type="file"
          accept={props.accept}
          multiple
          class="hidden"
          onChange={onChosen}
        />
      </div>
      <span class="text-xs text-neutral-500">
        Uploaded to the {props.label} group after the device is registered.
      </span>
    </div>
  );
};
